import { cn } from "@/lib/utils";
import { HTMLAttributes, forwardRef } from "react";

interface GlassCardProps extends HTMLAttributes<HTMLDivElement> {
    variant?: "default" | "neon" | "holographic";
    hoverEffect?: boolean;
}

export const GlassCard = forwardRef<HTMLDivElement, GlassCardProps>(
    ({ className, variant = "default", hoverEffect = true, children, ...props }, ref) => {
        return (
            <div
                ref={ref}
                className={cn(
                    "relative overflow-hidden rounded-xl border border-white/5 bg-card/40 backdrop-blur-xl transition-all duration-300",
                    hoverEffect && "hover:border-primary/50 hover:shadow-[0_0_30px_-5px_hsl(var(--primary)/0.3)] hover:-translate-y-1",
                    variant === "neon" && "border-primary/20 shadow-[0_0_15px_-3px_hsl(var(--primary)/0.2)]",
                    variant === "holographic" && "bg-gradient-to-br from-white/5 to-white/0 border-white/10 shadow-lg backdrop-blur-2xl",
                    className
                )}
                {...props}
            >
                {/* Shine effect */}
                <div className="absolute inset-0 z-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                {/* Content */}
                <div className="relative z-10">
                    {children}
                </div>
            </div>
        );
    }
);
GlassCard.displayName = "GlassCard";

export const GlassHeader = forwardRef<
    HTMLDivElement,
    HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        className={cn("flex flex-col space-y-1.5 p-6", className)}
        {...props}
    />
));
GlassHeader.displayName = "GlassHeader";

export const GlassTitle = forwardRef<
    HTMLParagraphElement,
    HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
    <h3
        ref={ref}
        className={cn(
            "font-display text-2xl font-semibold leading-none tracking-tight text-foreground",
            className
        )}
        {...props}
    />
));
GlassTitle.displayName = "GlassTitle";

export const GlassContent = forwardRef<
    HTMLDivElement,
    HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
));
GlassContent.displayName = "GlassContent";
